import { useCallback } from 'react';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { EntityRepositoryByEntityName } from '../../../classes/entities/AppEntityManager';
import { AppEntityManagerEntityTypesByEntityName } from '../../../classes/entities/AppEntityManagerEntityTypesByEntityName';
import {
  Callback,
  useComputedEntityResult
} from '../useComputedEntityResult/useComputedEntityResult';

export function useEntities<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
>(
  entityName: TEntityName,
  functionName: TFunctionName,
  ...functionArgs: GetRepositoryFunctionParams<TEntityName, TFunctionName>
): GetRepositoryFunctionReturnType<TEntityName, TFunctionName> {
  const callback = useCallback<
    Callback<
      [TEntityName],
      GetRepositoryFunctionReturnType<TEntityName, TFunctionName>
    >
  >(
    ({ repositories }) => {
      return (repositories[0] as any)[functionName](...functionArgs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...functionArgs, functionName]
  );

  return useComputedEntityResult({
    entityNames: [entityName],
    callback,
    resultsAreEqual: (a, b) => {
      return ArrayUtils.areShallowEqual(a, b);
    }
  });
}

type RepositoryFunctionKeys<TEntityName extends EntityName> = keyof {
  [key in keyof EntityRepositoryByEntityName[TEntityName] as IsValidRepositoryFunctionName<
    TEntityName,
    key
  >]: any;
};

type IsValidRepositoryFunctionName<
  TEntityName extends EntityName,
  TFunctionName extends keyof EntityRepositoryByEntityName[TEntityName]
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => Array<AppEntityManagerEntityTypesByEntityName[TEntityName]['entity']>
  ? TFunctionName
  : never;

type GetRepositoryFunctionParams<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => any
  ? Parameters<EntityRepositoryByEntityName[TEntityName][TFunctionName]>
  : never;

type GetRepositoryFunctionReturnType<
  TEntityName extends EntityName,
  TFunctionName extends RepositoryFunctionKeys<TEntityName>
> = EntityRepositoryByEntityName[TEntityName][TFunctionName] extends (
  ...args: Array<any>
) => any
  ? ReturnType<EntityRepositoryByEntityName[TEntityName][TFunctionName]>
  : never;
